import { Flow } from '@dynamic-flow/types';
import React from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { buildFormThemeStyles } from '../utils/formTheme';
import { FormStepLayout } from './FormStepLayout';
import { PageContent, SuccessFallback } from './PageContent';

// Loading State Component
export const LoadingState: React.FC<{
  message?: string;
  flow?: Flow | null;
  flowId?: string;
}> = ({ message = 'Loading form...', flow = null, flowId }) => {
  const { theme, primaryColor, mergedCSS, backgroundColor } =
    buildFormThemeStyles(flow, flowId);

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor }}
    >
      <style dangerouslySetInnerHTML={{ __html: mergedCSS }} />
      <div className="text-center">
        <LoadingSpinner color={primaryColor} className="mb-4" />
        <p style={{ color: theme.colors.text.secondary }}>{message}</p>
      </div>
    </div>
  );
};

// Error State Component
interface ErrorStateProps {
  error:
    | string
    | {
        message: string;
        details?: string;
        reason?: string;
        metadata?: any;
      };
  title?: string;
  onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error }) => {
  // Handle both string and object error formats
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorDetails = typeof error === 'string' ? undefined : error.details;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Error Title */}
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {errorMessage}
        </h2>

        {/* Error Details */}
        {errorDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left">
            <p className="text-gray-600 text-sm">{errorDetails}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Success State Component
interface SuccessStateProps {
  flow: Flow;
  onReset?: () => void;
}

export const SuccessState: React.FC<SuccessStateProps> = ({
  flow,
  onReset,
}) => {
  const endPageNode = flow.nodes.find(
    (node) => node.type === 'end_page' || node.type === 'thank_you'
  );

  const footer =
    flow.settings.allowMultipleSubmissions && onReset ? (
      <div className="pt-6">
        <button onClick={onReset} className="btn-primary w-full">
          Submit Another Response
        </button>
      </div>
    ) : undefined;

  if (endPageNode) {
    const hasContent =
      endPageNode.data.coverImageUrl || endPageNode.data.markdownContent;

    return (
      <FormStepLayout centerContent footer={footer}>
        <PageContent
          label={endPageNode.data.label || 'Thank You!'}
          description={endPageNode.data.description}
          coverImageUrl={endPageNode.data.coverImageUrl}
          markdownContent={endPageNode.data.markdownContent}
          fallback={!hasContent ? <SuccessFallback /> : undefined}
        />
      </FormStepLayout>
    );
  }

  return (
    <FormStepLayout centerContent footer={footer}>
      <PageContent label="Thank You!" fallback={<SuccessFallback />} />
    </FormStepLayout>
  );
};

// Redirect State Component
export const RedirectState: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="form-step max-w-md w-full bg-white rounded-lg shadow-lg text-center">
      <div className="text-green-500 text-6xl mb-4">✅</div>
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h2>
      <p className="text-gray-600 mb-4">
        Your response has been submitted successfully.
      </p>
      <p className="text-sm text-gray-500">Redirecting...</p>
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mt-4"></div>
    </div>
  </div>
);

// No Data State Component
export const NoDataState: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <p className="text-gray-600">No form data available</p>
    </div>
  </div>
);
